import { useEffect, useRef } from 'react';
import { debounce } from 'lodash';
import { AnyMemo, Task } from 'models/Memo';
import { UseFormReturn } from 'react-hook-form';
import { saveComposerDraft } from 'xstate/actors/ComposerDraftActor';
import { FormValues } from './useFormInitialisation';

const DRAFT_SAVE_DELAY_MS = 500;

interface Props {
  memo: AnyMemo | null;
  boxId: string | undefined;
  watch: UseFormReturn<FormValues>['watch'];
  getValues: UseFormReturn<FormValues>['getValues'];
}

// Saves the plain composer fields (not applicable in edit mode) so they
// survive the composer unmounting — e.g. navigating to Search,
// Notifications, or Settings and back.
export function useComposerDraftAutosave({
  memo,
  boxId,
  watch,
  getValues,
}: Props) {
  const saveRef = useRef<ReturnType<typeof debounce> | null>(null);

  useEffect(() => {
    if (memo || !boxId) return;

    const save = debounce(() => {
      const {
        type,
        collectionId,
        title,
        content,
        description,
        source,
        author,
        tasks,
        name,
        isAllDay,
        isMoment,
        startAt,
        endAt,
        datedAt,
      } = getValues();
      saveComposerDraft(boxId, {
        type,
        collectionId,
        title,
        content,
        description,
        source,
        author,
        tasks: tasks as unknown as Task[],
        name,
        isAllDay,
        isMoment,
        startAt,
        endAt,
        datedAt,
      });
    }, DRAFT_SAVE_DELAY_MS);
    saveRef.current = save;

    const subscription = watch(() => save());
    return () => {
      subscription.unsubscribe();
      // Flush (not cancel) — unmounting is usually a navigation away, and
      // any pending change should be persisted, not discarded.
      save.flush();
      saveRef.current = null;
    };
  }, [memo, boxId, watch, getValues]);

  return { cancelPendingSave: () => saveRef.current?.cancel() };
}
